import { Phone, Mail, MapPin } from "lucide-react";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { FaqSection } from "./Faq";
import { Reveal } from "./anim";
import { CONTACT, IMAGES, SINCE, CONTACT_FAQ, breadcrumb, faqSchema } from "./marketingData";

export default function ContactPage() {
  return (
    <div className="bg-[#1A1A18] text-white font-body antialiased overflow-x-hidden" data-testid="contact-page">
      <Seo
        title={`Contact Indian Transport Organisation — Get a Freight Quote | since ${SINCE}`}
        description="Call or email Indian Transport Organisation for ODC, defence, machinery, oil & gas and air-cargo movements to any pincode in India. Head office in Mumbai."
        path="/contact"
        image={IMAGES.fleetYard}
        imageAlt="Indian Transport Organisation fleet yard"
        keywords="contact indian transport organisation, transport company contact mumbai, freight quote india, logistics enquiry india"
        jsonLd={[
          {
            "@context": "https://schema.org", "@type": "MovingCompany",
            name: "Indian Transport Organisation", url: "https://itoworldwide.com/contact",
            telephone: CONTACT.phone, email: CONTACT.email, foundingDate: "1972",
            address: { "@type": "PostalAddress", streetAddress: CONTACT.address, addressLocality: "Mumbai", addressRegion: "MH", postalCode: "400053", addressCountry: "IN" },
            areaServed: "IN",
          },
          breadcrumb([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
          faqSchema(CONTACT_FAQ),
        ]}
      />
      <MarketingNav heroDark />

      <section className="pt-36 pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">Head office · Mumbai</p>
            <h1 className="font-heading font-black tracking-tight text-4xl md:text-6xl leading-[1.05]">Tell us what needs moving.</h1>
            <p className="mt-5 text-white/85 max-w-md leading-relaxed">
              From a single machine spare to a fleet of tanks — call or email us and our team will quote
              your movement, by road or air, to any pincode in India.
            </p>
            <div className="mt-10 space-y-6">
              <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="w-12 h-12 rounded-full bg-[#C84B31] flex items-center justify-center shrink-0"><Phone className="w-5 h-5" /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-white/75">Call us</span>
                  <span className="text-xl font-heading font-bold group-hover:text-[#D4A373] transition-colors">{CONTACT.phone}</span>
                </span>
              </a>
              <a href={CONTACT.emailHref} data-testid="contact-email" className="flex items-center gap-4 group">
                <span className="w-12 h-12 rounded-full bg-[#C84B31] flex items-center justify-center shrink-0"><Mail className="w-5 h-5" /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-white/75">Email us</span>
                  <span className="text-lg font-heading font-bold group-hover:text-[#D4A373] transition-colors break-all">{CONTACT.email}</span>
                </span>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#D4A373]" /></span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-white/75">Head office</span>
                  <span className="text-base text-white/90">{CONTACT.address}</span>
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="rounded-2xl overflow-hidden border border-white/10 h-[420px]">
            <iframe title="ITO head office location" data-testid="contact-map"
              className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              src={
                process.env.REACT_APP_GOOGLE_MAPS_API_KEY
                  ? `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(CONTACT.mapQuery)}&zoom=17`
                  : `https://maps.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&t=&z=17&ie=UTF8&iwloc=&output=embed`
              } />
          </Reveal>
        </div>
      </section>

      <FaqSection faqs={CONTACT_FAQ} dark title="Contact — frequently asked questions" testid="contact-faq" />

      <MarketingFooter />
    </div>
  );
}
